import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Outlet, useLocation } from 'react-router';
import type { PublicUser } from '@gitrader/shared';
import { Logo } from '../components/ui';
import { resolve, type ResolvedRoute } from '../routes/helpers';
import { ROUTE_TREE } from '../routes/tree';
import type { TradingEnv } from '../routes/types';
import { useFullscreen } from './fullscreen';
import { FullscreenBar } from './FullscreenBar';
import { deploymentLabel } from './live/EnvBadge';
import { LiveBanner } from './live/LiveBanner';
import { HelpPanel } from '../help/HelpPanel';
import { HelpProvider } from '../help/HelpProvider';
import { MobileTabBar } from './MobileTabBar';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { useCurrentRoute } from './useCurrentRoute';

export const RAIL_STORAGE_KEY = 'gitrader.rail';
const RAIL_EXPANDED = '248px';
const RAIL_COLLAPSED = '60px';

function readRail(): boolean {
  try {
    return window.localStorage.getItem(RAIL_STORAGE_KEY) === 'collapsed';
  } catch {
    return false;
  }
}

/**
 * The application frame every screen lives inside.
 *
 *   ≥ 901px   brand | top bar          ≤ 900px   top bar
 *             nav   | main                       main
 *                                                bottom tab bar; nav = drawer + scrim
 *
 * Full-screen chart mode replaces the chrome with a compact bar; `main` keeps its place
 * in the tree so the page inside it is never remounted by the transition.
 */
export function AppShell({ user, onSignOut }: { user: PublicUser; onSignOut: () => void }) {
  const { pathname } = useLocation();
  const { route, chain } = useCurrentRoute();
  const { active: fullscreen } = useFullscreen();

  const [collapsed, setCollapsed] = useState(readRail);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleRail = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try {
        window.localStorage.setItem(RAIL_STORAGE_KEY, next ? 'collapsed' : 'expanded');
      } catch {
        // per-viewer convenience only
      }
      return next;
    });
  }, []);

  // Route change closes the drawer and resets the scroll of the page container.
  useEffect(() => {
    setDrawerOpen(false);
    const main = document.getElementById('main');
    if (main) main.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const resolvedChain = useMemo<ResolvedRoute[]>(
    () =>
      chain
        .map((_, i) => resolve(ROUTE_TREE, chainPath(chain.slice(0, i + 1))))
        .filter((r): r is ResolvedRoute => r !== undefined),
    [chain],
  );

  const env: TradingEnv =
    route?.env === 'live' || chain.some((n) => n.env === 'live') ? 'live' : 'paper';
  const deployment = deploymentLabel(window.location.hostname);

  useEffect(() => {
    const label = route?.node.label ?? 'Not found';
    document.title = env === 'live' ? `LIVE · ${label} · GiTrader` : `${label} · GiTrader`;
  }, [route, env]);

  const style = { '--rail': collapsed ? RAIL_COLLAPSED : RAIL_EXPANDED } as CSSProperties;

  return (
    // The provider wraps the whole frame because both the top bar's ? and every inline ? on a
    // page drive the same panel; two providers would be two panels that disagreed.
    <HelpProvider>
      <div
        data-shell
        data-fullscreen={fullscreen ? 'true' : 'false'}
        data-env={env}
        style={style}
        className={`grid h-full min-h-0 grid-cols-1 grid-rows-[auto_minmax(0,1fr)] ${
          fullscreen ? '' : 'desk:grid-cols-[var(--rail)_minmax(0,1fr)]'
        }`}
      >
        {fullscreen ? null : (
          <div
            data-brand
            className="hidden h-[52px] items-center border-r border-b border-border bg-surface px-3.5 desk:col-start-1 desk:row-start-1 desk:flex"
          >
            <Logo compact={collapsed} />
          </div>
        )}

        {fullscreen ? (
          <FullscreenBar env={env} deployment={deployment} />
        ) : (
          <div className="col-start-1 row-start-1 min-w-0 desk:col-start-2">
            <TopBar
              user={user}
              env={env}
              deployment={deployment}
              chain={chain}
              resolvedChain={resolvedChain}
              collapsed={collapsed}
              onToggleRail={toggleRail}
              onOpenDrawer={() => setDrawerOpen(true)}
              onSignOut={onSignOut}
            />
          </div>
        )}

        {fullscreen ? null : (
          <div className="desk:col-start-1 desk:row-start-2 desk:h-full desk:min-h-0">
            <Sidebar
              role={user.role}
              collapsed={collapsed}
              drawerOpen={drawerOpen}
              onCloseDrawer={() => setDrawerOpen(false)}
              activeChain={chain}
            />
          </div>
        )}

        <main
          id="main"
          data-main
          className={`col-start-1 row-start-2 min-h-0 min-w-0 ${
            fullscreen ? 'overflow-hidden' : 'overflow-x-hidden overflow-y-auto desk:col-start-2'
          }`}
        >
          {env === 'live' ? <LiveBanner /> : null}
          <div
            className={
              fullscreen
                ? 'h-full'
                : // Every page gets the whole window.
                  //
                  // This used to cap at 1180px with an opt-out per route, on the reasoning that a
                  // reading measure beats a wall of text. The reasoning was sound and the shape
                  // was wrong: almost nothing in this app is prose. The pages are tables of
                  // sessions, coverage and jobs, and capping them put a horizontal scrollbar
                  // inside a 910px column on an 1800px monitor — the data hidden behind a
                  // scrollbar with half the screen empty beside it.
                  //
                  // Reading width still matters, so it is enforced where the prose actually is:
                  // `PageHeader` caps its lede at 62ch, and a form that should not stretch sets
                  // its own width. Both are local decisions by the thing that knows, rather than
                  // one number applied to every page whether it is prose or not.
                  'mx-auto w-full px-4 pt-4 pb-[calc(84px+env(safe-area-inset-bottom))] desk:px-[22px] desk:pt-5 desk:pb-16'
            }
          >
            <Outlet />
          </div>
        </main>

        {fullscreen ? null : <MobileTabBar role={user.role} activeChain={chain} />}

        {/* Last, so the slide-over sits above the shell without needing a stacking context of
          its own. It renders nothing until something opens it. */}
        <HelpPanel />
      </div>
    </HelpProvider>
  );
}

function chainPath(nodes: readonly { path: string }[]): string {
  const segs = nodes.map((n) => n.path).filter(Boolean);
  return segs.length ? `/${segs.join('/')}` : '/';
}
